import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';
import { isActiveDealerPro, isApprovedSupplier } from '../../util/ctWholesale';
import { showsLoadBoardLink } from '../../util/ctLoadBoard';

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: <FormattedMessage id="UserNav.yourListings" />,
          selected: currentPage === 'ManageListingsPage',
          linkProps: {
            name: 'ManageListingsPage',
          },
        },
      ]
    : [];

  // Shown to approved wholesale suppliers only. The pages themselves are guarded by the server.
  const currentUser = useSelector(state => state.user?.currentUser);
  const wholesaleTabMaybe = isApprovedSupplier(currentUser)
    ? [
        {
          text: 'Wholesale inventory',
          selected: currentPage === 'WholesaleInventoryPage',
          linkProps: { name: 'WholesaleInventoryPage' },
        },
      ]
    : [];

  // Active Dealer Pro members (wholesale buyers).
  const wholesaleBuyerTabMaybe = isActiveDealerPro(currentUser)
    ? [
        {
          text: 'Wholesale containers',
          selected: currentPage === 'WholesaleBrowsePage',
          linkProps: { name: 'WholesaleBrowsePage' },
        },
      ]
    : [];

  // Dealer Pro members and Carrier accounts. The board itself checks access on the server.
  const loadBoardTabMaybe = showsLoadBoardLink(currentUser)
    ? [
        {
          text: 'Load board',
          selected: currentPage === 'LoadBoardPage',
          linkProps: { name: 'LoadBoardPage' },
        },
      ]
    : [];

  const tabs = [
    ...manageListingsTabMaybe,
    ...wholesaleBuyerTabMaybe,
    ...loadBoardTabMaybe,
    ...wholesaleTabMaybe,
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
